import React, { useEffect, useState } from "react";
import { GraduationCap, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CareerReportTab from "@/components/career/CareerReportTab";
import AnalyticsTab from "@/components/career/AnalyticsTab";
import ResumeTab from "@/components/career/ResumeTab";
import LinkedInTab from "@/components/career/LinkedInTab";
import RoadmapTab from "@/components/career/RoadmapTab";

export default function CareerCenterPage() {
  const [tab, setTab] = useState("report");
  const [report, setReport] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [interviewAnalytics, setInterviewAnalytics] = useState(null);
  const [resume, setResume] = useState(null);
  const [linkedin, setLinkedin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regeneratingReport, setRegeneratingReport] = useState(false);
  const [regeneratingLinkedin, setRegeneratingLinkedin] = useState(false);

  useEffect(() => {
    api.get("/career/report").then((r) => setReport(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === "analytics" && !analytics) {
      api.get("/analytics/me").then((r) => setAnalytics(r.data));
      api.get("/interview/analytics/me").then((r) => setInterviewAnalytics(r.data)).catch(() => setInterviewAnalytics(null));
    }
    if (tab === "resume" && !resume) api.get("/resume/data").then((r) => setResume(r.data));
    if (tab === "linkedin" && !linkedin) api.get("/linkedin/content").then((r) => setLinkedin(r.data));
  }, [tab, analytics, resume, linkedin]);

  const regenerateReport = async () => {
    setRegeneratingReport(true);
    try {
      const { data } = await api.get("/career/report?force=true");
      setReport(data);
    } finally {
      setRegeneratingReport(false);
    }
  };

  const regenerateLinkedin = async () => {
    setRegeneratingLinkedin(true);
    try {
      const { data } = await api.get("/linkedin/content?force=true");
      setLinkedin(data);
    } finally {
      setRegeneratingLinkedin(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="career-center-loading"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="career-center-page">
      <div className="flex items-center gap-3 mb-2">
        <GraduationCap className="w-6 h-6 text-indigo-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">AI-Powered</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Career Center</h1>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">Your personalized AI career coach — job readiness score, learning analytics, an auto-generated resume, and ready-to-post LinkedIn content.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="career-tabs">
          <TabsTrigger value="report" data-testid="career-tab-report">Career Report</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="career-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="resume" data-testid="career-tab-resume">Resume</TabsTrigger>
          <TabsTrigger value="linkedin" data-testid="career-tab-linkedin">LinkedIn</TabsTrigger>
          <TabsTrigger value="roadmap" data-testid="career-tab-roadmap">AI Roadmap</TabsTrigger>
        </TabsList>
        <TabsContent value="report" className="mt-6">
          <CareerReportTab report={report} onRegenerate={regenerateReport} regenerating={regeneratingReport} />
        </TabsContent>
        <TabsContent value="analytics" className="mt-6">
          {analytics ? <AnalyticsTab analytics={analytics} interviewAnalytics={interviewAnalytics} /> : (
            <div className="flex items-center justify-center py-16" data-testid="career-analytics-loading"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>
          )}
        </TabsContent>
        <TabsContent value="resume" className="mt-6">
          {resume ? <ResumeTab resume={resume} /> : (
            <div className="flex items-center justify-center py-16" data-testid="career-resume-loading"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>
          )}
        </TabsContent>
        <TabsContent value="linkedin" className="mt-6">
          {linkedin ? <LinkedInTab content={linkedin} onRegenerate={regenerateLinkedin} regenerating={regeneratingLinkedin} /> : (
            <div className="flex items-center justify-center py-16" data-testid="career-linkedin-loading"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>
          )}
        </TabsContent>
        <TabsContent value="roadmap" className="mt-6"><RoadmapTab /></TabsContent>
      </Tabs>
    </div>
  );
}
